import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

interface Message {
  id: string;
  timestamp: Date;
  direction: 'incoming' | 'outgoing';
  action?: string;
  payload?: any;
  messageId?: string;
  result?: any;
  response?: any;
  responseTimestamp?: Date;
  hasResponse?: boolean;
}

interface MessageLoggerProps {
  messages: Message[];
}

const MessageLogger: React.FC<MessageLoggerProps> = ({ messages }) => {
  const [filter, setFilter] = useState<'all' | 'incoming' | 'outgoing'>('all');
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());

  const filteredMessages = messages.filter(message => 
    filter === 'all' || message.direction === filter
  );

  const toggleMessageExpansion = (messageId: string) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };

  const formatJson = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">OCPP Messages</h3>
          <div className="flex space-x-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'incoming' | 'outgoing')}
              className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="all">All Messages</option>
              <option value="outgoing">Outgoing</option>
              <option value="incoming">Incoming</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="max-h-96 overflow-y-auto">
        {filteredMessages.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            No messages to display
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredMessages.map((message) => (
              <div key={message.id} className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        message.direction === 'outgoing' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {message.direction === 'outgoing' ? '→' : '←'} {message.action || 'Response'}
                      </span>
                      <span className="text-sm text-gray-500">
                        {message.timestamp.toLocaleTimeString()}
                      </span>
                      {message.hasResponse && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ Responded
                        </span>
                      )}
                      {message.messageId && (
                        <span className="text-xs text-gray-400 font-mono">
                          {message.messageId.substring(0, 8)}
                        </span>
                      )}
                    </div>
                    
                    <button
                      onClick={() => toggleMessageExpansion(message.id)}
                      className="mt-2 flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
                    >
                      {expandedMessages.has(message.id) ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                      <span>
                        {expandedMessages.has(message.id) ? 'Hide' : 'Show'} details
                      </span>
                    </button>
                    
                    {expandedMessages.has(message.id) && (
                      <div className="mt-3 space-y-3">
                        {/* Request/Outgoing Payload */}
                        {message.payload && (
                          <div>
                            <h5 className="text-xs font-medium text-blue-700 mb-1 flex items-center">
                              <span className="mr-1">→</span>
                              {message.direction === 'outgoing' ? 'Request Payload' : 'Incoming Payload'}:
                            </h5>
                            <pre className="text-xs bg-blue-50 p-2 rounded border-l-2 border-blue-200 overflow-x-auto">
                              {formatJson(message.payload)}
                            </pre>
                          </div>
                        )}

                        {/* Response Payload */}
                        {message.response && (
                          <div>
                            <div className="flex items-center justify-between">
                              <h5 className="text-xs font-medium text-green-700 mb-1 flex items-center">
                                <span className="mr-1">←</span>
                                Response Payload:
                              </h5>
                              {message.responseTimestamp && (
                                <span className="text-xs text-gray-500">
                                  {message.responseTimestamp.toLocaleTimeString()}
                                </span>
                              )}
                            </div>
                            <pre className="text-xs bg-green-50 p-2 rounded border-l-2 border-green-200 overflow-x-auto">
                              {formatJson(message.response)}
                            </pre>
                          </div>
                        )}

                        {/* Legacy result field for backward compatibility */}
                        {message.result && !message.response && (
                          <div>
                            <h5 className="text-xs font-medium text-gray-700 mb-1">Result:</h5>
                            <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                              {formatJson(message.result)}
                            </pre>
                          </div>
                        )}

                        {/* Show if no response yet for outgoing messages */}
                        {message.direction === 'outgoing' && !message.hasResponse && !message.response && (
                          <div className="text-xs text-gray-500 italic border-l-2 border-gray-200 pl-2">
                            Waiting for response...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageLogger;